import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { GlassPanel } from "../components/GlassPanel";
import {
  Calendar as CalendarIcon, Clock, Car, MessageSquare,
  ChevronLeft, ChevronRight, User as UserIcon, Play, CheckCircle2,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { subMeta } from "../lib/pipeline";
import { toast } from "sonner";

/**
 * Installer-facing daily schedule. Groups all upcoming timeline slots by day
 * and lists them chronologically. Admin can pick any installer via a header
 * dropdown; installers only see their own schedule.
 */
export default function SchedulePage({ embedded = false }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();

  const [installers, setInstallers] = useState([]);
  const [installerId, setInstallerId] = useState(isAdmin ? "" : user?.id || "");
  const [schedule, setSchedule] = useState([]);
  const [dayOffset, setDayOffset] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      api.get("/users").then(({ data }) => {
        const list = data.filter((u) => u.role === "installer" || u.role === "assistant");
        setInstallers(list);
        if (!installerId && list.length > 0) setInstallerId(list[0].id);
      }).catch(() => {});
    } else {
      setInstallerId(user?.id || "");
    }
  }, [isAdmin]);

  const loadSchedule = () => {
    if (!installerId) return;
    setLoading(true);
    api.get(`/installers/${installerId}/schedule`)
      .then(({ data }) => setSchedule(data || []))
      .catch((err) => toast.error(err.response?.data?.detail || "Failed to load schedule"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadSchedule(); }, [installerId]);

  // Flatten timeline slots across all jobs
  const slots = useMemo(() => {
    const out = [];
    for (const job of schedule) {
      for (const s of (job.slots || [])) {
        out.push({ ...s, _job: job });
      }
    }
    out.sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""));
    return out;
  }, [schedule]);

  // Focus on a specific day, defaulting to today. Installers can arrow through days.
  const focusDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + dayOffset);
    return d;
  }, [dayOffset]);

  const focusYmd = ymd(focusDate);
  const daySlots = slots.filter((s) => s.start_at && ymd(new Date(s.start_at)) === focusYmd);

  // Also list jobs the installer owns a full stage on (no time slot needed)
  const stageOwnedJobs = schedule.filter(
    (j) => (j.stages_owned || []).length > 0 || (!j.slots?.length && j.job_id),
  );

  const openJobThread = async (jobId) => {
    try {
      await api.post(`/chat-v2/job/${jobId}/channel`);
      navigate("/", { state: { openChannel: `job:${jobId}` } });
      toast.info("Job thread opened in Dashboard chat");
    } catch (err) { toast.error("Could not open thread"); }
  };

  const clockSlot = async (jobId, slotId, action) => {
    try {
      const { data } = await api.post(`/jobs/${jobId}/timeline/${slotId}/clock`, { action });
      const label = action === "start" ? "Clocked in" : "Clocked out";
      const sub = data?.job?.sub_status;
      toast.success(sub ? `${label} — job now ${sub.replace(/_/g, " ")}` : label);
      loadSchedule();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Clock action failed");
    }
  };

  const humanDay = focusDate.toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric",
  });
  const isToday = dayOffset === 0;

  return (
    <div className="space-y-5">
      {/* Header — hidden when embedded inside Dashboard so we don't
          double up the page title. Admin installer-picker still renders
          inline so the Dashboard version stays useful. */}
      {!embedded && (
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-label flex items-center gap-2"><CalendarIcon className="w-3 h-3" /> My schedule</div>
            <h1 className="font-heading text-4xl font-light tracking-tighter text-white">Schedule</h1>
            <p className="text-slate-400 mt-1.5 text-sm">
              {isAdmin ? "Pick an installer to see their day." : "Your assigned time slots — tap a slot to see the job."}
            </p>
          </div>
          {isAdmin && (
            <select
              data-testid="schedule-installer-picker"
              value={installerId}
              onChange={(e) => setInstallerId(e.target.value)}
              className="bg-black/40 border border-white/10 rounded-full px-4 py-2 text-sm text-white focus:outline-none focus:border-white/30"
            >
              {installers.map((u) => (
                <option key={u.id} value={u.id} className="bg-neutral-900">{u.name || u.email}</option>
              ))}
            </select>
          )}
        </div>
      )}
      {embedded && isAdmin && (
        <div className="flex items-center justify-between gap-3">
          <div className="text-label flex items-center gap-2">
            <CalendarIcon className="w-3 h-3" /> Schedule
          </div>
          <select
            data-testid="schedule-installer-picker"
            value={installerId}
            onChange={(e) => setInstallerId(e.target.value)}
            className="bg-black/40 border border-white/10 rounded-full px-3 py-1.5 text-xs text-white focus:outline-none focus:border-white/30"
          >
            {installers.map((u) => (
              <option key={u.id} value={u.id} className="bg-neutral-900">{u.name || u.email}</option>
            ))}
          </select>
        </div>
      )}

      {/* Day toolbar */}
      <GlassPanel className="!p-4" animate={false}>
        <div className="flex items-center justify-between gap-3">
          <button
            data-testid="schedule-prev-day"
            onClick={() => setDayOffset((o) => o - 1)}
            className="w-9 h-9 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 flex items-center justify-center text-slate-200"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="text-center flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              {isToday ? "Today" : dayOffset > 0 ? `In ${dayOffset} day${dayOffset === 1 ? "" : "s"}` : `${Math.abs(dayOffset)} day${dayOffset === -1 ? "" : "s"} ago`}
            </div>
            <div data-testid="schedule-day-label" className="font-heading text-lg md:text-2xl text-white truncate">{humanDay}</div>
          </div>
          <button
            data-testid="schedule-next-day"
            onClick={() => setDayOffset((o) => o + 1)}
            className="w-9 h-9 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 flex items-center justify-center text-slate-200"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        {!isToday && (
          <div className="text-center mt-2">
            <button
              data-testid="schedule-today"
              onClick={() => setDayOffset(0)}
              className="text-xs text-blue-300 hover:text-blue-200"
            >
              Jump to today
            </button>
          </div>
        )}
      </GlassPanel>

      {/* Day slots */}
      <div className="space-y-3">
        {loading && (
          <GlassPanel className="!p-6 text-center text-slate-500 italic" animate={false}>
            Loading schedule…
          </GlassPanel>
        )}
        {!loading && daySlots.length === 0 && (
          <GlassPanel className="!p-6 text-center text-slate-500 italic" animate={false} data-testid="schedule-empty">
            Nothing scheduled for this day. Enjoy the breather.
          </GlassPanel>
        )}
        {daySlots.map((s) => {
          const j = s._job;
          const stageLabel = subMeta("job", s.stage).label;
          return (
            <GlassPanel
              key={s.id}
              className="!p-0 overflow-hidden"
              data-testid={`schedule-slot-${s.id}`}
              animate={false}
            >
              <div className="flex">
                {/* Time rail */}
                <div className="w-24 shrink-0 bg-gradient-to-b from-blue-500/20 to-emerald-500/10 border-r border-white/10 p-3 flex flex-col items-center justify-center">
                  <div className="font-heading text-lg text-white leading-none">{timeOnly(s.start_at)}</div>
                  <div className="text-[10px] text-slate-400 my-1">to</div>
                  <div className="font-heading text-sm text-slate-200 leading-none">{timeOnly(s.end_at)}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-2">{durationHours(s.start_at, s.end_at)}</div>
                </div>
                {/* Body */}
                <div className="flex-1 p-4 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="pill pill-info text-[10px]">{stageLabel}</span>
                    <span className="text-[10px] text-slate-500 uppercase tracking-widest">Job {j.main_status}/{j.sub_status}</span>
                  </div>
                  <div className="text-white font-heading text-lg truncate flex items-center gap-1.5">
                    <Car className="w-4 h-4 text-slate-400 shrink-0" /> {j.vehicle || "—"}
                  </div>
                  <div className="text-sm text-slate-300 truncate">{j.service || "—"}</div>
                  {j.contact_name && (
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><UserIcon className="w-3 h-3" /> {j.contact_name}</div>
                  )}
                  {s.notes && (
                    <div className="mt-2 text-xs text-slate-400 whitespace-pre-wrap bg-white/[0.03] rounded-xl px-3 py-2 border border-white/5">
                      {s.notes}
                    </div>
                  )}
                  <div className="mt-3 flex gap-2 flex-wrap items-center">
                    {s.status === "in_progress" ? (
                      <button
                        data-testid={`schedule-clock-finish-${s.id}`}
                        onClick={() => clockSlot(j.job_id, s.id, "finish")}
                        className="text-xs flex items-center gap-1 bg-emerald-500 text-black font-semibold rounded-full px-3 py-1.5 hover:bg-emerald-400"
                      >
                        <CheckCircle2 className="w-3 h-3" /> Clock out · finish
                      </button>
                    ) : s.status === "done" ? (
                      <span
                        data-testid={`schedule-clock-done-${s.id}`}
                        className="text-xs flex items-center gap-1 bg-emerald-500/10 text-emerald-300 rounded-full px-3 py-1.5 border border-emerald-400/20"
                      >
                        <CheckCircle2 className="w-3 h-3" /> Finished{s.ended_at ? ` · ${timeOnly(s.ended_at)}` : ""}
                      </span>
                    ) : (
                      <button
                        data-testid={`schedule-clock-start-${s.id}`}
                        onClick={() => clockSlot(j.job_id, s.id, "start")}
                        className="text-xs flex items-center gap-1 bg-white text-black font-semibold rounded-full px-3 py-1.5 hover:bg-slate-100"
                      >
                        <Play className="w-3 h-3" /> Start
                      </button>
                    )}
                    {s.started_at && s.status === "in_progress" && (
                      <span data-testid={`schedule-clock-running-${s.id}`} className="text-[10px] text-slate-400">Running since {timeOnly(s.started_at)}</span>
                    )}
                    <button
                      data-testid={`schedule-open-jobs-${s.id}`}
                      onClick={() => navigate("/jobs")}
                      className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 rounded-full px-3 py-1.5"
                    >
                      Open in Jobs
                    </button>
                    <button
                      data-testid={`schedule-thread-${s.id}`}
                      onClick={() => openJobThread(j.job_id)}
                      className="text-xs flex items-center gap-1 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-400/20 text-blue-300 rounded-full px-3 py-1.5"
                    >
                      <MessageSquare className="w-3 h-3" /> Chat thread
                    </button>
                  </div>
                </div>
              </div>
            </GlassPanel>
          );
        })}
      </div>

      {/* Stage-owned jobs section removed — installers already see every
          job they're assigned to on the Jobs tab (My Jobs). Keeping the same
          list here caused duplicated cards. */}
    </div>
  );
}

// ---- helpers -------------------------------------------------------------

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function timeOnly(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch { return "—"; }
}

function durationHours(a, b) {
  if (!a || !b) return "";
  const ms = new Date(b) - new Date(a);
  if (ms <= 0) return "";
  const h = ms / 3600000;
  if (h >= 1) return `${Math.round(h * 10) / 10}h`;
  const m = Math.round(ms / 60000);
  return `${m}m`;
}
